import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

const rawApiUrl = import.meta.env.VITE_API_URL as string | undefined;
const BASE_URL = rawApiUrl
  ? rawApiUrl.startsWith('http')
    ? rawApiUrl          // already has https://
    : `https://${rawApiUrl}`  // missing scheme — add it
  : '/api';              // dev fallback (Vite proxy)

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

// Module-level facility override (set by FacilityContext when admin switches facility)
let _facilityIdOverride: number | null = null;
export function setFacilityOverride(id: number | null) {
  _facilityIdOverride = id;
}

// Request interceptor — attach Bearer token + optional facility override header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (_facilityIdOverride !== null) {
      config.headers['X-Facility-ID'] = String(_facilityIdOverride);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const original = error.config;

    const isLoginRequest = original.url?.includes('/auth/login');

    if (error.response?.status === 401 && !original._retry && !isLoginRequest) {
      original._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        try {
          const res = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
          const { accessToken } = res.data.data;
          localStorage.setItem('accessToken', accessToken);
          original.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(original);
        } catch {
          // Refresh failed — clear tokens and redirect
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Origin of the Railway API server (BASE_URL minus its trailing /api) -- used by
// utils/imageUrl.ts's getImageSrc to resolve relative /uploads/* paths served by the server's own
// express.static('/uploads') mount, which lives at this origin but NOT under /api. In dev BASE_URL
// is the '/api' Vite-proxy fallback, so this is '' and relative paths pass through unchanged
// (vite.config.ts already proxies /uploads/* the same way it proxies /api).
export const API_ORIGIN = BASE_URL.replace(/\/api\/?$/, '');

// ─── Typed API helpers ────────────────────────────────────────────────────────
export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<{ success: boolean; data: T }>(url, { params }).then((r) => r.data),

  post: <T>(url: string, data?: unknown) =>
    apiClient.post<{ success: boolean; data: T }>(url, data).then((r) => r.data),

  put: <T>(url: string, data?: unknown) =>
    apiClient.put<{ success: boolean; data: T }>(url, data).then((r) => r.data),

  patch: <T>(url: string, data?: unknown) =>
    apiClient.patch<{ success: boolean; data: T }>(url, data).then((r) => r.data),

  delete: <T>(url: string) =>
    apiClient.delete<{ success: boolean; data?: T; message?: string }>(url).then((r) => r.data),

  postForm: <T>(url: string, formData: FormData) =>
    apiClient.post<{ success: boolean; data: T }>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),

  patchForm: <T>(url: string, formData: FormData) =>
    apiClient.patch<{ success: boolean; data: T }>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
};
